import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { httpsCallable } from 'firebase/functions'
import { Layout } from '../components/layout/Layout'
import { ManagedSeo } from '../components/seo/ManagedSeo'
import { buildCourseSchema, AH_ORGANIZATION, SCOTT_PERSON } from '../config/schema'
import { assets } from '../config/assets'
import { functions } from '../lib/functions'
import { logGaEvent } from '../lib/analytics'
import { useNextClassDate } from '../hooks/useNextClassDate'
import {
  SmsConsentCheckbox,
  SMS_CONSENT_TEXT,
  SMS_CONSENT_VERSION,
} from '../components/forms/SmsConsentCheckbox'

// Course, not Product. A Zoom class is not merchandise, and declaring it as
// Product made Google demand shipping and returns and then reject the listing.
// See buildCourseSchema in config/schema.ts.
const ACCELERATOR_COURSE_SCHEMA = buildCourseSchema({
  name: 'Confidence Accelerator Class',
  description:
    'Live weekly Zoom coaching every Wednesday at 6pm EST with Scott Purdum. 12-week class with direct feedback, workbooks, and a private community.',
  url: 'https://www.advantagehorsemanship.com/accelerator',
  repeatCount: 12,
  offers: [{ price: '750.00' }],
})

type Plan = 'full' | 'plan'

// Funnel instrumentation. Both plans total $750; 'plan' is the same price
// financed over three months, so GA `value` is identical and only item_variant
// distinguishes them.
const ACCELERATOR_PRICE = 750

function acceleratorItems(plan: Plan) {
  return [{
    // Same slug the purchase event reports (PRICE_TO_SLUG on the server).
    item_id: 'confidence-accelerator',
    item_name: 'Confidence Accelerator Class',
    item_variant: plan,
    price: ACCELERATOR_PRICE,
    quantity: 1,
  }]
}

interface AcceleratorLeadPayload {
  firstName: string
  email: string
  phone: string
  plan: Plan
  smsConsent?: boolean
  smsConsentText?: string
  smsConsentVersion?: string
}

interface CheckoutResult {
  url: string | null
}

const submitLead = httpsCallable<AcceleratorLeadPayload, CheckoutResult>(
  functions,
  'submitAcceleratorLead',
)

const WHO_FOR = [
  "You've been riding for years but still don't feel confident or in control",
  "You understand your horse isn't dangerous, but your body still reacts like he is",
  'You want structured coaching, not more YouTube videos',
  "You're ready to do the work and you want someone to hold you accountable",
]

const CURRICULUM = [
  {
    title: 'Fitness for Equestrians',
    body: 'The complete fitness program is included at no additional charge. Start as soon as you enroll and keep access after your class ends.',
  },
  {
    title: 'Weekly Live Coaching',
    body: '60-minute Zoom sessions every Wednesday at 6pm EST with Scott directly',
  },
  {
    title: 'Session Recordings',
    body: 'Every call recorded and available in your member portal within 24 hours',
  },
  {
    title: 'Private Community Access',
    body: 'Class-only community room for questions, wins, and accountability between sessions',
  },
  {
    title: 'Workbooks and Guides',
    body: 'Digital workbooks and guides delivered to your member portal',
  },
  {
    title: "Rider's Way App",
    body: 'Full app access included for the duration of the program',
  },
]

const FOUR_FACTORS = [
  {
    number: '01',
    label: 'Action Before Feeling',
    body: 'You will not wait to feel ready. You take the action, and the feeling follows. Real confidence is built in that order, and it starts week one.',
  },
  {
    number: '02',
    label: 'Getting Out of Your Own Way',
    body: 'Before technique, before strategy, we work on the fear response that tightens you up the moment your horse shifts under you. Learning to manage your fear and stay grounded is the foundation everything else is built on.',
  },
  {
    number: '03',
    label: 'Accountability and Hard Work',
    body: 'This is not a passive class. You will have work to do between sessions. If you show up and put in the reps, the shift shows up.',
  },
  {
    number: '04',
    label: 'Stacking Wins',
    body: 'Every week builds on the last. Small wins compound into real change. By the end, you will not recognize the rider you were in week one.',
  },
]

const TESTIMONIALS = [
  {
    quote:
      'My horse Bandit would not back off pressure no matter what I tried. Week three Scott walked me through a groundwork reset and by the next ride Bandit was responding to my seat alone. Twenty years of riding and nobody showed me that.',
    name: 'Linda Kowalczyk',
    location: 'Colorado Springs, Colorado',
  },
  {
    quote:
      'I brought a specific problem to the Wednesday call — my mare bolting at canter transitions. Scott had me video it and broke down exactly where I was bracing. Fixed it in one session.',
    name: 'Michelle Reyes',
    location: 'Nashville, Tennessee',
  },
  {
    quote:
      'I was ready to quit riding after a bad fall. Scott did not sugarcoat it, but he gave me a structured path back. I am cantering again and I trust myself more than I did before the fall.',
    name: 'Donna Saunders',
    location: 'Tampa, Florida',
  },
]

const INCLUDES = [
  '12 weekly live sessions',
  'All recordings',
  'Digital workbooks and guides delivered to your member portal',
  "Rider's Way App included",
  'Private class community',
]

const STATIC_FAQS: { q: string; a: string }[] = [
  {
    q: 'How long is the program?',
    a: 'The Confidence Accelerator runs for 12 weeks. You meet live every Wednesday at 6pm EST.',
  },
  {
    q: 'What if I miss a session?',
    a: 'Every session is recorded and posted to your member portal within 24 hours. You will not fall behind.',
  },
  {
    q: 'Is this for beginners?',
    a: 'The program works for any experience level. What matters is that you are ready to be honest about where you are and committed to doing the work.',
  },
  {
    q: 'What is the difference between pay in full and the payment plan?',
    a: 'Same program, same access. Pay in full saves you money versus the three-payment option. The payment plan makes it accessible if you prefer to spread the cost.',
  },
]

type FormStatus = 'idle' | 'loading' | 'error'

function CheckIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
      className="flex-shrink-0 mt-0.5"
    >
      <circle cx="10" cy="10" r="9" stroke="#C8A84B" strokeWidth="1.5" />
      <path
        d="M6.5 10l2.5 2.5 4.5-5"
        stroke="#C8A84B"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function SmallCheckIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      className="flex-shrink-0"
    >
      <path
        d="M3 8l3.5 3.5L13 5"
        stroke="#C8A84B"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function AcceleratorPage() {
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<Plan>('full')
  const [firstName, setFirstName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [smsConsent, setSmsConsent] = useState(false)
  const [status, setStatus] = useState<FormStatus>('idle')
  const [errorMessage, setErrorMessage] = useState('')
  const [openFaq, setOpenFaq] = useState<number | null>(null)

  // Shared with the homepage and /start so all three agree.
  const { label: nextClassDate } = useNextClassDate()

  const nextClassDisplay = nextClassDate ?? 'Coming Soon'

  const FAQS = [
    ...STATIC_FAQS,
    {
      q: 'When does the next class start?',
      a: nextClassDate
        ? `The next class starts ${nextClassDate}. Registration closes once spots are filled.`
        : 'Registration opens soon. Join the waitlist to be notified.',
    },
  ]

  function openModal(plan: Plan) {
    setSelectedPlan(plan)
    setModalOpen(true)
    setStatus('idle')
    setErrorMessage('')
    // Step 2 of 4. See HorseSensePage for the reasoning behind this funnel.
    logGaEvent('select_item', {
      item_list_name: 'Confidence Accelerator',
      items: acceleratorItems(plan),
    })
  }

  function closeModal() {
    setModalOpen(false)
    setFirstName('')
    setEmail('')
    setPhone('')
    setSmsConsent(false)
    setStatus('idle')
    setErrorMessage('')
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setStatus('loading')
    setErrorMessage('')

    // Step 3 of 4: handing off to Stripe.
    logGaEvent('begin_checkout', {
      currency: 'USD',
      value: ACCELERATOR_PRICE,
      items: acceleratorItems(selectedPlan),
    })

    try {
      const result = await submitLead({
        firstName: firstName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        plan: selectedPlan,
        // Consent only counts when they gave a number AND ticked the box.
        // Neither one alone is permission to text.
        smsConsent: smsConsent && phone.trim().length > 0,
        smsConsentText:
          smsConsent && phone.trim().length > 0 ? SMS_CONSENT_TEXT : undefined,
        smsConsentVersion:
          smsConsent && phone.trim().length > 0 ? SMS_CONSENT_VERSION : undefined,
      })
      const url = result.data.url
      if (url) {
        window.location.href = url
      }
    } catch {
      setStatus('error')
      setErrorMessage(
        'Something went wrong. Please try again or email support@advantagehorsemanship.com.',
      )
    }
  }

  function scrollToCurriculum() {
    document.getElementById('curriculum')?.scrollIntoView({ behavior: 'smooth' })
  }

  const isDisabled = status === 'loading' || !firstName.trim() || !email.trim()

  return (
    <Layout>
      <ManagedSeo path="/accelerator" jsonLd={[ACCELERATOR_COURSE_SCHEMA, AH_ORGANIZATION, SCOTT_PERSON]} />
      {/* ─── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative bg-navy overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${assets.clinics.indoorRiding})` }}
        />
        <div className="absolute inset-0 bg-navy/85" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="max-w-3xl">
            <p className="font-body text-gold text-sm font-semibold tracking-widest uppercase mb-4">
              Confidence Accelerator Class
            </p>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl text-white leading-tight mb-6">
              Stop Riding on Guesswork. Start Building Real Confidence.
            </h1>
            <p className="font-body text-lg text-white/80 leading-relaxed mb-8 max-w-2xl">
              The Confidence Accelerator is a live weekly class that gives you
              the system, the feedback, and the accountability to become the
              rider you've been trying to become for years.
            </p>
            <div className="flex flex-wrap gap-6 mb-10">
              <span className="font-body text-gold text-sm font-semibold">
                Every Wednesday at 6pm EST
              </span>
              <span className="font-body text-gold text-sm font-semibold">
                Next Class: {nextClassDisplay}
              </span>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => openModal('full')}
                style={{
                  backgroundColor: '#C8A84B',
                  color: '#0A1628',
                  fontWeight: '700',
                  padding: '16px 36px',
                  fontSize: '16px',
                  border: 'none',
                  cursor: 'pointer',
                  minHeight: '52px',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                }}
              >
                Reserve Your Spot
              </button>
              <button
                onClick={scrollToCurriculum}
                className="font-body text-sm font-medium text-white hover:text-gold transition-colors inline-flex items-center justify-center min-h-[52px] px-6 border border-white/30 hover:border-gold/50"
              >
                See What's Included
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ─── WHO THIS IS FOR ──────────────────────────────────────────────── */}
      <section className="bg-white py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-4xl sm:text-5xl text-navy mb-12 text-center">
            This Program Is For You If&hellip;
          </h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {WHO_FOR.map((statement, i) => (
              <div key={i} className="flex gap-4 items-start">
                <CheckIcon />
                <p className="font-body text-base text-navy/80 leading-relaxed">
                  {statement}
                </p>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <button
              onClick={() => openModal('full')}
              style={{
                backgroundColor: '#C8A84B',
                color: '#0A1628',
                fontWeight: '700',
                padding: '16px 36px',
                fontSize: '16px',
                border: 'none',
                cursor: 'pointer',
                minHeight: '52px',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
              }}
            >
              Reserve Your Spot
            </button>
          </div>
        </div>
      </section>

      {/* ─── CURRICULUM ───────────────────────────────────────────────────── */}
      <section id="curriculum" className="bg-navy py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="font-body text-gold text-sm font-semibold tracking-widest uppercase mb-3">
              What You Get
            </p>
            <h2 className="font-heading text-4xl sm:text-5xl text-white">
              What's Included in the Accelerator
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {CURRICULUM.map((item) => (
              <div
                key={item.title}
                className="border-t-2 border-gold pt-6 pb-6 px-6"
                style={{ backgroundColor: '#0D1F3C' }}
              >
                <h3 className="font-heading text-2xl text-white mb-3">
                  {item.title}
                </h3>
                <p className="font-body text-sm text-white/70 leading-relaxed">
                  {item.body}
                </p>
              </div>
            ))}
          </div>

          {/* Coaching action photo */}
          <div className="mt-12 overflow-hidden rounded-lg">
            <img
              src={assets.clinics.indoorRiding}
              alt="Scott Purdum coaching riders during a live Accelerator session"
              width={1200}
              height={600}
              loading="lazy"
              className="w-full max-h-[280px] object-cover object-center"
            />
          </div>
        </div>
      </section>

      {/* ─── CONFIDENCE IS BUILT, NOT FOUND ───────────────────────────────── */}
      <section className="bg-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl sm:text-5xl text-navy mb-4">
              Confidence Is Built, Not Found
            </h2>
            <p className="font-body text-navy/60 text-base max-w-2xl mx-auto leading-relaxed">
              Confidence is not a feeling you wait for. It's the byproduct of
              the right action, taken consistently, until your body stops
              tensing up the moment something changes in the saddle. This class
              is the system that builds it.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10">
            {FOUR_FACTORS.map((factor) => (
              <div key={factor.label} className="text-center lg:text-left">
                <div className="font-heading text-6xl text-gold/25 leading-none mb-3 select-none">
                  {factor.number}
                </div>
                <h3 className="font-heading text-3xl text-navy mb-3">
                  {factor.label}
                </h3>
                <p className="font-body text-sm text-navy/70 leading-relaxed">
                  {factor.body}
                </p>
              </div>
            ))}
          </div>
          <p className="font-body italic text-navy/70 text-base max-w-2xl mx-auto leading-relaxed text-center mt-12">
            This class is for riders ready to do the work. If that's you, you
            will leave a different rider than the one who signed up.
          </p>
        </div>
      </section>

      {/* ─── TESTIMONIALS ─────────────────────────────────────────────────── */}
      <section className="py-16 lg:py-24" style={{ backgroundColor: '#F5F5F5' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-4xl sm:text-5xl text-navy text-center mb-12">
            What Accelerator Graduates Are Saying
          </h2>
          <div className="grid lg:grid-cols-3 gap-8">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="bg-white p-8">
                <div className="font-heading text-5xl text-gold/30 leading-none mb-4 select-none">
                  &ldquo;
                </div>
                <blockquote className="font-body text-base text-navy/80 leading-relaxed mb-6">
                  {t.quote}
                </blockquote>
                <p className="font-body text-sm font-semibold text-navy">
                  {t.name}
                </p>
                <p className="font-body text-xs text-navy/50">{t.location}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── PRICING ──────────────────────────────────────────────────────── */}
      <section className="bg-navy py-16 lg:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="font-body text-gold text-sm font-semibold tracking-widest uppercase mb-3">
              Enrollment
            </p>
            <h2 className="font-heading text-4xl sm:text-5xl text-white">
              Join the Next Class
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            {/* Card 1 -Pay in Full */}
            <div className="bg-black/40 border border-white/10 p-8 flex flex-col">
              <div className="mb-4">
                <span className="font-body text-xs font-semibold text-gold tracking-widest uppercase">
                  Best Value
                </span>
              </div>
              <div className="mb-2">
                <span className="font-body text-6xl text-white font-bold">$750</span>
              </div>
              <p className="font-body text-white/50 text-sm mb-6">
                One payment. Full access. Save vs the payment plan.
              </p>
              <ul className="space-y-2 mb-8 flex-1">
                {INCLUDES.map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <SmallCheckIcon />
                    <span className="font-body text-sm text-white/70">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => openModal('full')}
                style={{
                  backgroundColor: '#C8A84B',
                  color: '#0A1628',
                  fontWeight: '700',
                  padding: '16px 24px',
                  fontSize: '15px',
                  border: 'none',
                  cursor: 'pointer',
                  width: '100%',
                  minHeight: '52px',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                }}
              >
                Enroll Now, Pay in Full
              </button>
            </div>

            {/* Card 2 -Payment Plan */}
            <div className="bg-black/40 border border-white/10 p-8 flex flex-col">
              <div className="mb-4">
                <span className="font-body text-xs font-semibold text-white/40 tracking-widest uppercase">
                  3-Month Plan
                </span>
              </div>
              <div className="mb-2">
                <span className="font-body text-6xl text-white font-bold">$250</span>
                <span className="font-body text-white/50 text-base"> / month</span>
              </div>
              <p className="font-body text-white/50 text-sm mb-6">
                Three monthly payments. Same full access.
              </p>
              <ul className="space-y-2 mb-8 flex-1">
                {INCLUDES.map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <SmallCheckIcon />
                    <span className="font-body text-sm text-white/70">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => openModal('plan')}
                style={{
                  backgroundColor: 'transparent',
                  color: '#C8A84B',
                  fontWeight: '700',
                  padding: '16px 24px',
                  fontSize: '15px',
                  border: '2px solid #C8A84B',
                  cursor: 'pointer',
                  width: '100%',
                  minHeight: '52px',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                }}
              >
                Enroll Now, Payment Plan
              </button>
            </div>
          </div>

          <p className="font-body text-gold text-sm text-center font-semibold">
            Next class starts {nextClassDisplay}. Spots are limited.
          </p>
        </div>
      </section>

      {/* ─── FAQ ──────────────────────────────────────────────────────────── */}
      <section className="bg-white py-16 lg:py-24">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-4xl sm:text-5xl text-navy text-center mb-12">
            Common Questions
          </h2>
          <div className="divide-y divide-navy/10">
            {FAQS.map((faq, i) => (
              <div key={i}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full text-left py-5 flex items-start justify-between gap-4 min-h-[52px]"
                  aria-expanded={openFaq === i}
                >
                  <span className="font-body text-base font-semibold text-navy">
                    {faq.q}
                  </span>
                  <span
                    className={`flex-shrink-0 text-gold mt-0.5 transition-transform duration-200 ${
                      openFaq === i ? 'rotate-45' : ''
                    }`}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M10 4v12M4 10h12"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                </button>
                {openFaq === i && (
                  <div className="pb-5">
                    <p className="font-body text-sm text-navy/70 leading-relaxed">
                      {faq.a}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA BANNER ─────────────────────────────────────────────── */}
      <section className="py-16 lg:py-24" style={{ backgroundColor: '#C8A84B' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-5xl sm:text-6xl text-navy mb-4">
            The Next Class Starts {nextClassDisplay}. Don&rsquo;t Wait.
          </h2>
          <p className="font-body text-navy/70 text-lg mb-8">
            Spots are limited. Once the class is full, registration closes.
          </p>
          <button
            onClick={() => openModal('full')}
            style={{
              backgroundColor: '#0A1628',
              color: '#FFFFFF',
              fontWeight: '700',
              padding: '18px 48px',
              fontSize: '16px',
              border: 'none',
              cursor: 'pointer',
              minHeight: '52px',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
            }}
          >
            Reserve Your Spot
          </button>
          <p className="font-body text-xs text-navy/50 mt-4">
            Questions?{' '}
            <Link
              to="/contact"
              className="underline hover:text-navy transition-colors"
            >
              Contact us
            </Link>
          </p>
        </div>
      </section>

      {/* ─── LEAD CAPTURE MODAL ───────────────────────────────────────────── */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-start sm:items-center justify-center overflow-y-auto bg-black/70 px-4 py-8"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal()
          }}
        >
          <div className="relative my-auto flex max-h-[calc(100dvh-4rem)] w-full max-w-md flex-col overflow-hidden bg-white">
            {/* Modal header */}
            <div className="flex-shrink-0 bg-navy px-8 py-6 flex items-center justify-between">
              <div>
                <h2 className="font-heading text-2xl text-white">
                  Reserve Your Spot
                </h2>
                <p className="font-body text-xs text-gold mt-1">
                  {selectedPlan === 'full'
                    ? 'Pay in Full, $750'
                    : 'Payment Plan, $250 × 3 months'}
                </p>
              </div>
              <button
                onClick={closeModal}
                className="text-white/60 hover:text-white transition-colors ml-4 flex-shrink-0"
                aria-label="Close"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>

            {/* Modal form */}
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-8 py-8">
              <p className="font-body text-sm text-navy/70 mb-6">
                Enter your details and you'll be taken directly to a secure
                checkout to complete your enrollment.
              </p>

              <div className="space-y-4 mb-6">
                <div>
                  <label
                    className="block font-body text-xs font-semibold text-navy/60 uppercase tracking-wide mb-1"
                    htmlFor="accel-first-name"
                  >
                    First Name *
                  </label>
                  <input
                    id="accel-first-name"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    autoComplete="given-name"
                    className="w-full border border-navy/20 px-4 py-3 font-body text-navy focus:outline-none focus:border-navy"
                    style={{ fontSize: '16px' }}
                    placeholder="Your first name"
                  />
                </div>

                <div>
                  <label
                    className="block font-body text-xs font-semibold text-navy/60 uppercase tracking-wide mb-1"
                    htmlFor="accel-email"
                  >
                    Email Address *
                  </label>
                  <input
                    id="accel-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="w-full border border-navy/20 px-4 py-3 font-body text-navy focus:outline-none focus:border-navy"
                    style={{ fontSize: '16px' }}
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label
                    className="block font-body text-xs font-semibold text-navy/60 uppercase tracking-wide mb-1"
                    htmlFor="accel-phone"
                  >
                    Mobile Phone{' '}
                    <span className="text-navy/40 normal-case font-normal tracking-normal">
                      (optional)
                    </span>
                  </label>
                  <input
                    id="accel-phone"
                    name="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    autoComplete="tel"
                    maxLength={50}
                    className="w-full border border-navy/20 px-4 py-3 font-body text-navy focus:outline-none focus:border-navy"
                    style={{ fontSize: '16px' }}
                    placeholder="(540) 555-0123"
                  />
                  <p className="font-body text-xs text-navy/50 mt-1 leading-relaxed">
                    Add your mobile number for class reminders by text. You can
                    enroll without it.
                  </p>
                </div>

                {/* A2P 10DLC: its own box, unchecked, never required. */}
                <div className="pt-2 border-t border-navy/10">
                  <SmsConsentCheckbox
                    id="accel-sms-consent"
                    checked={smsConsent}
                    onChange={setSmsConsent}
                  />
                </div>
              </div>

              {status === 'error' && errorMessage && (
                <p className="font-body text-sm text-red-600 mb-4">
                  {errorMessage}
                </p>
              )}

              <button
                type="submit"
                disabled={isDisabled}
                style={{
                  backgroundColor: isDisabled ? '#a08940' : '#C8A84B',
                  color: '#0A1628',
                  fontWeight: '700',
                  padding: '16px 32px',
                  fontSize: '16px',
                  border: 'none',
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  width: '100%',
                  minHeight: '52px',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                }}
              >
                {status === 'loading'
                  ? 'Redirecting to checkout...'
                  : 'Proceed to Secure Checkout →'}
              </button>

              <p className="font-body text-xs text-navy/40 mt-3 text-center">
                Powered by Stripe · Secure checkout
              </p>
            </form>
          </div>
        </div>
      )}
    </Layout>
  )
}
